import { type ICianMFUIElement } from '@cian/mf-registry/browser';
import { type IMicrofrontendManifest } from '@cian/mf-registry/shared';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { registerNewbuildingCallbackWidgetManifest } from 'shared/services/registerNewbuildingCallbackWidgetManifest';
import {
  type INewbuildingCallbackWidgetMicrofrontendAPI,
  type IOpenWidgetParams,
  type TOpenWidgetResultHandler,
} from 'shared/types/microfrontends/newbuildingCallbackWidget';
import { useApplicationContext } from 'shared/utils/applicationContext';

interface IUseNewbuildingCallbackWidgetResult {
  openWidget: (params: IOpenWidgetParams) => Promise<void>;
  widgetManifest: IMicrofrontendManifest | undefined;
  widgetRef: RefObject<ICianMFUIElement | null>;
}

export const useNewbuildingCallbackWidget = (
  resultCallback: TOpenWidgetResultHandler,
): IUseNewbuildingCallbackWidgetResult => {
  const { httpApi, logger, microfrontendsRegistry } = useApplicationContext();

  const widgetRef = useRef<ICianMFUIElement>(null);

  const [widgetManifest, setWidgetManifest] = useState<IMicrofrontendManifest>();

  const getLocalAPI = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    (): INewbuildingCallbackWidgetMicrofrontendAPI => (widgetRef.current?.instance?.customElement as any)?.api,
    [widgetRef],
  );

  const paramsRef = useRef<IOpenWidgetParams>(undefined);
  const firstAPICallRef = useRef(false);

  useEffect(() => {
    // Логика выполняется один раз при первом вызове локального API после установки манифеста в стейт.
    if (widgetManifest && !firstAPICallRef.current) {
      firstAPICallRef.current = true;

      // Хак, чтобы подождать рендера `<Microfrontend />` и инициализации рефа для локального API.
      setTimeout(() => {
        const api = getLocalAPI();

        if (api) {
          api['/v1/open'](paramsRef.current as IOpenWidgetParams);
          resultCallback({ isError: false });
        } else {
          resultCallback({ isError: true });
        }
      }, 0);
    }
  }, [resultCallback, getLocalAPI, logger, widgetManifest]);

  const registerAndSetManifest = useCallback(
    async (params: IOpenWidgetParams) => {
      const result = await registerNewbuildingCallbackWidgetManifest({
        httpApi,
        logger,
        microfrontendsRegistry,
      });

      if (result) {
        // Сохраняем в реф, чтобы вызвать с этими параметрами после установки в стейт и инициализации рефа.
        paramsRef.current = params;

        setWidgetManifest(result);
      } else {
        resultCallback({ isError: true });
      }
    },
    [httpApi, logger, microfrontendsRegistry, resultCallback],
  );

  const openWidget = useCallback(
    async (params: IOpenWidgetParams): Promise<void> => {
      if (widgetManifest) {
        await getLocalAPI()['/v1/open'](params);
        resultCallback({ isError: false });
      } else {
        await registerAndSetManifest(params);
      }
    },
    [getLocalAPI, registerAndSetManifest, resultCallback, widgetManifest],
  );

  return {
    openWidget,
    widgetManifest,
    widgetRef,
  };
};
